class ToggleLike {
  private readonly nonce: string;
  private readonly ajaxUrl: string;
  private debounceTimer: number | null = null;
  private readonly debounceDelay = 300; // ms
  private readonly animationDuration = 300; // ms

  constructor(nonce: string, ajaxUrl: string) {
    this.nonce = nonce;
    this.ajaxUrl = ajaxUrl;
    this.init();
  }

  private init(): void {
    document.addEventListener('DOMContentLoaded', () => {
      document
        .querySelectorAll<HTMLButtonElement>('[data-like]')
        .forEach((button) => {
          button.addEventListener('click', (event) =>
            this.handleLikeClick(event, button)
          );
        });
    });
  }

  private debounce(func: () => void): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = window.setTimeout(func, this.debounceDelay);
  }

  private handleLikeClick(event: Event, button: HTMLButtonElement): void {
    event.preventDefault();

    if (button.disabled) return;

    button.disabled = true;
    this.animateClick(button);

    this.debounce(() => this.toggleLike(button));
  }

  private animateClick(button: HTMLButtonElement): void {
    button.classList.add('clicked');
    setTimeout(
      () => button.classList.remove('clicked'),
      this.animationDuration
    );
  }

  private toggleLike(button: HTMLButtonElement): void {
    const postId = button.getAttribute('data-like');
    if (!postId) {
      button.disabled = false;
      return;
    }

    const formData = new FormData();
    formData.append('action', 'toggle_like');
    formData.append('post_id', postId);
    formData.append('nonce', this.nonce);

    fetch(this.ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      body: formData
    })
      .then(this.handleResponse)
      .then((data: { count: number; user_liked: boolean }) => {
        this.updateButton(button, data);
        this.animateResponse(button, data.user_liked);
      })
      .catch(this.handleError)
      .finally(() => {
        button.disabled = false;
      });
  }

  private handleResponse(response: Response): Promise<any> {
    if (!response.ok) {
      throw new Error('Erreur HTTP, statut = ' + response.status);
    }
    return response.json();
  }

  private updateButton(
    button: HTMLButtonElement,
    data: { count: number; user_liked: boolean }
  ): void {
    const postId = button.getAttribute('data-like');
    if (!postId) return;

    // Sélectionner tous les boutons avec le même data-like
    const allButtons = document.querySelectorAll<HTMLButtonElement>(
      `[data-like="${postId}"]`
    );

    allButtons.forEach((btn) => {
      const countElement = btn.querySelector<HTMLElement>('[data-like-count]');
      if (countElement) {
        // Ajouter un "+" seulement si l'utilisateur a liké
        if (data.user_liked) {
          countElement.textContent = `+${data.count}`;

          // Retirer le "+" après 1 seconde
          setTimeout(() => {
            countElement.textContent = data.count.toString();
          }, 1000);
        } else {
          // Si unlike, mettre simplement à jour le compteur
          countElement.textContent = data.count.toString();
        }
      }
      btn.classList.toggle('liked', data.user_liked);
    });
  }

  private animateResponse(button: HTMLButtonElement, liked: boolean): void {
    button.classList.add(liked ? 'liked-animation' : 'unliked-animation');
    setTimeout(() => {
      button.classList.remove('liked-animation', 'unliked-animation');
    }, this.animationDuration);
  }

  private handleError(error: Error): void {
    console.error('Erreur:', error);
  }
}

// Utilisation de la classe
const { nonce, ajax_url } = (window as any).ajax;
new ToggleLike(nonce, ajax_url);
